import { useClasser } from "@code-hike/classer";
import * as React from "react";
import { ErrorOverlay } from "../../common/ErrorOverlay";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import { OpenInCodeSandboxButton } from "../../common/OpenInCodeSandboxButton";
import { SandpackStack } from "../../common/Stack";
import { useSandpack } from "../../hooks/useSandpack";
import { generateRandomId } from "../../utils/stringUtils";
import { Navigator } from "../Navigator";
import { RefreshButton } from "./RefreshButton";
export { RefreshButton };
export const SandpackPreview = ({ customStyle, showNavigator = false, showRefreshButton = true, showOpenInCodeSandbox = true, showSandpackErrorOverlay = true, viewportSize = "auto", viewportOrientation = "portrait", }) => {
    const { sandpack, listen } = useSandpack();
    const [iframeComputedHeight, setComputedAutoHeight] = React.useState(null);
    const { status, registerBundler, unregisterBundler, errorScreenRegisteredRef, openInCSBRegisteredRef, loadingScreenRegisteredRef, } = sandpack;
    const c = useClasser("sp");
    const clientId = React.useRef(generateRandomId());
    const iframeRef = React.useRef(null);
    // SandpackPreview immediately registers the custom screens/components so the bundler does not render any of them
    openInCSBRegisteredRef.current = true;
    errorScreenRegisteredRef.current = true;
    loadingScreenRegisteredRef.current = true;
    React.useEffect(() => {
        const iframeElement = iframeRef.current;
        registerBundler(iframeElement, clientId.current);
        const unsub = listen((message) => {
            if (message.type === "resize") {
                setComputedAutoHeight(message.height);
            }
        }, clientId.current);
        return () => {
            unsub();
            unregisterBundler(clientId.current);
        };
    }, []);
    const handleNewURL = (newUrl) => {
        if (!iframeRef.current) {
            return;
        }
        iframeRef.current.src = newUrl;
    };
    const viewportStyle = computeViewportSize(viewportSize, viewportOrientation);
    return (React.createElement(SandpackStack, { customStyle: {
            ...customStyle,
            ...viewportStyle,
            display: status !== "idle" ? "flex" : "none",
        } },
        showNavigator ? (React.createElement(Navigator, { clientId: clientId.current, onURLChange: handleNewURL })) : null,
        React.createElement("div", { className: c("preview-container") },
            React.createElement("iframe", { ref: iframeRef, className: c("preview-iframe"), style: {
                    // set height based on the content only in auto mode
                    // and when the computed height was returned by the bundler
                    height: viewportSize === "auto" && iframeComputedHeight
                        ? iframeComputedHeight
                        : undefined,
                }, title: "Sandpack Preview" }),
            showSandpackErrorOverlay ? React.createElement(ErrorOverlay, null) : null,
            React.createElement("div", { className: c("preview-actions") },
                !showNavigator && showRefreshButton && status === "running" ? (React.createElement(RefreshButton, { clientId: clientId.current })) : null,
                showOpenInCodeSandbox ? React.createElement(OpenInCodeSandboxButton, null) : null),
            React.createElement(LoadingOverlay, { clientId: clientId.current }))));
};
const VIEWPORT_SIZE_PRESET_MAP = {
    "iPhone X": { x: 375, y: 812 },
    iPad: { x: 768, y: 1024 },
    "Pixel 2": { x: 411, y: 731 },
    "Moto G4": { x: 360, y: 640 },
    "Surface Duo": { x: 540, y: 720 },
};
const computeViewportSize = (viewport, orientation) => {
    if (viewport === "auto") {
        return {};
    }
    if (typeof viewport === "string") {
        const { x, y } = VIEWPORT_SIZE_PRESET_MAP[viewport];
        return orientation === "portrait"
            ? { width: x, height: y }
            : { width: y, height: x };
    }
    return viewport;
};
//# sourceMappingURL=index.js.map